// eslint-disable-next-line import/named
import { SortableEvent } from 'sortablejs';
import {
  WidgetJSONData,
  addWidgetToLayout,
  addWidgetToLayoutFromDrawer,
  changeArrayIndex,
  // changeArrayIndex,
} from '@/components/WidgetMaker/WidgetDnD/widgetDnDUtils';
import { fetchStoreInfoBySubDomain, getDraftThemeComponents } from '@/redux/actions';
import { useDispatch } from 'react-redux';
import { IWidgetDndActions, IWidgetDndHelpers, IWidgetDndInitialState } from '../types';
import { useSSRSelector } from '@/redux/ssrStore';
import { ADMIN_WIDGETS } from 'src/constants/widgets';
import cloneDeep from 'lodash/cloneDeep';
import { getStoreSubscriptionConditions } from 'src/utils/storeSubscriptionConfigs';
import { isThemeBrandWebsite } from 'src/utils/isThemeBrandWebsite';

export const DEFAULT_WIDGET_DND_CONTEXT_HELPERS: IWidgetDndHelpers = {
  toggleSidePanelState: () => {},
  handleWidgetPropChangeForBasicWidgets: () => {},
  handlePublish: () => {},
  handleWidgetPropChange: () => {},
  handleWidgetAddFromDrawerWithConfig: () => {},
  addWidget: () => {},
  handleWidgetAdd: () => {},
  addWidgetViaMainSection: () => {},
  handleWidgetSelect: () => {},
  handleBuilderSectionSelect: () => {},
  handleDeleteIconClick: () => {},
  removeWidgetRecursive: () => {},
  removeWidget: () => {},
  handleDelete: () => {},
  handleUpDownMove: () => {},
  handleWidgetAddFromDrawer: () => {},
  handleReorderWidgetSidebar: () => {},
  redirectToWidgetOutline: () => {},
  getWidgetsOnCurrentRoute: () => {},
  getStylesOnCurrentRoute: () => {},
  getBuilderConfigsOnCurrentRoute: () => {},
  handleRouteStyleChange: () => {},
};

interface IWidgetHelpersActions {
  widgetContextState: IWidgetDndInitialState;
  widgetContextActions: IWidgetDndActions;
}

export const useWidgetContextHelpers = (
  argues: IWidgetHelpersActions
): IWidgetDndHelpers => {
  const reduxDispatch = useDispatch();
  const { storeInfo } = useSSRSelector((state) => ({
    storeInfo: state.storeReducer.store,
  }));
  const subscriptionStatus = getStoreSubscriptionConditions(storeInfo);

  const { widgetContextState, widgetContextActions } = argues;

  const {
    setActiveItem,
    // setWidgets,
    setWidgetsByRoute,
    setOpenPostPublishModal,
    setHasUnsavedChanges,
    setSidePanelState,
    setActiveWidget,
    setIndexToAddWidgetAtFromMainSection,
    setIsHeaderFooterPropertyActive,
    setWidgetIdToDelete,
    setWidgetDeleteModalOpen,
  } = widgetContextActions;

  const getWidgetsOnCurrentRoute = () => {
    const currentSelectedWidgets =
      widgetContextState.widgetsByRoute[widgetContextState.currentSelectedRoute]?.widgets;
    return currentSelectedWidgets;
  };

  const getStylesOnCurrentRoute = () => {
    const currentSelectedWidgets =
      widgetContextState.widgetsByRoute[widgetContextState.currentSelectedRoute]?.style;
    return currentSelectedWidgets;
  };

  const getBuilderConfigsOnCurrentRoute = () => {
    const currentSelectedWidgets =
      widgetContextState.widgetsByRoute[widgetContextState.currentSelectedRoute];
    return currentSelectedWidgets;
  };

  const handleWidgetPropChangeForBasicWidgets = (
    values: Record<string, any>,
    widgetId?: string,
    flushAll?: boolean
  ) => {
    widgetId = widgetId || widgetContextState.activeWidgetId;
    const activeWidgetData = getWidgetsOnCurrentRoute()[widgetId];
    let newActiveWidgetProps: Record<string, any> = {};
    if (flushAll) {
      newActiveWidgetProps = { ...values };
    } else {
      newActiveWidgetProps = {
        ...activeWidgetData.props,
        ...values,
      };
    }
    const newWidgetsData: WidgetJSONData = {
      ...activeWidgetData,
      props: newActiveWidgetProps,
    };
    const newWidgets = { ...getWidgetsOnCurrentRoute(), [widgetId]: newWidgetsData };
    setWidgetsByRoute({
      routeName: widgetContextState.currentSelectedRoute,
      routeConfigs: {
        ...widgetContextState.widgetsByRoute[widgetContextState.currentSelectedRoute],
        widgets: newWidgets,
      },
    });
    // setWidgets(newWidgets);
    setHasUnsavedChanges(true);
  };

  const toggleSidePanelState = (type, leftPanelBuilderConfigID = null) => {
    const refreshedState = {
      showWidgetOutline: false,
      showAddWidgetDrawerSection: false,
      showActiveWidgetProperty: false,
      showStyleLayout: false,
      showStyleByRouteOutline: false,
      config: { leftPanelBuilderConfigID: null },
    };

    switch (type) {
      case 'widgetOutline':
        refreshedState.showWidgetOutline = true;
        break;
      case 'widgetDrawerSection':
        refreshedState.showAddWidgetDrawerSection = true;
        break;
      case 'widgetProperty':
        refreshedState.showActiveWidgetProperty = true;
        break;
      case 'styleLayout':
        refreshedState.showStyleLayout = true;
        break;
      case 'stylePropertyByRoute':
        refreshedState.showStyleByRouteOutline = true;
        refreshedState.config.leftPanelBuilderConfigID = leftPanelBuilderConfigID;
        break;
    }

    setSidePanelState({ ...widgetContextState.sidePanelState, ...refreshedState });
  };

  const handlePublish = () => {
    toggleSidePanelState('widgetOutline');
    setOpenPostPublishModal(true);
    setActiveItem({ outline: true, styleTemplate: false });
    setHasUnsavedChanges(false);
    if (subscriptionStatus?.isOldSubscriptionActive)
      reduxDispatch(fetchStoreInfoBySubDomain(storeInfo?.domain));
    else {
      if (isThemeBrandWebsite()) return;
      reduxDispatch(getDraftThemeComponents(storeInfo?.store_id));
    }
  };

  const handleWidgetPropChange = (values: Record<string, any>, widgetId?: string) => {
    setWidgetsByRoute(({ widgetsByRoute, currentSelectedRoute }) => {
      widgetId = widgetId || widgetContextState.activeWidgetId;
      const activeWidgetData = getWidgetsOnCurrentRoute()[widgetId];
      const newActiveWidgetProps: Record<string, any> = {
        ...activeWidgetData?.props,
        ...cloneDeep(values?.props ? values?.props : values),
      };
      const newWidgetsData: WidgetJSONData = {
        ...activeWidgetData,
        props: cloneDeep(newActiveWidgetProps),
      };
      const newWidgets = { ...getWidgetsOnCurrentRoute(), [widgetId]: newWidgetsData };
      return {
        routeName: currentSelectedRoute,
        routeConfigs: {
          ...widgetsByRoute[currentSelectedRoute],
          widgets: newWidgets,
        },
      };
    });
    // setWidgets(newWidgets);
    setHasUnsavedChanges(true);
  };

  const handleRouteStyleChange = (params = {} as any) => {
    const { styles = {} } = params;
    setWidgetsByRoute(({ widgetsByRoute, currentSelectedRoute }) => {
      const newStyles = cloneDeep(widgetsByRoute?.[currentSelectedRoute]?.style);
      return {
        routeName: currentSelectedRoute,
        routeConfigs: {
          ...widgetsByRoute[currentSelectedRoute],
          style: { ...newStyles, ...cloneDeep(styles) },
        },
      };
    });
    // setWidgets(newWidgets);
    setHasUnsavedChanges(true);
  };

  const addWidget = (addedWidgetsData: WidgetJSONData | WidgetJSONData[]) => {
    setWidgetsByRoute(({ widgetsByRoute, currentSelectedRoute }) => {
      const oldWidgets = widgetsByRoute[currentSelectedRoute]?.widgets;
      const widgetsDataArr = Array.isArray(addedWidgetsData)
        ? addedWidgetsData
        : [addedWidgetsData];
      widgetsDataArr.forEach((widgetData) => {
        const newIndex =
          widgetData.insertIndex >= 0
            ? widgetData.insertIndex
            : oldWidgets[widgetData.parent]?.children.length;
        oldWidgets[widgetData.parent]?.children.splice(newIndex, 0, widgetData.id);
        oldWidgets[widgetData.id] = widgetData;
      });
      return {
        routeName: currentSelectedRoute,
        routeConfigs: {
          ...widgetsByRoute[currentSelectedRoute],
          widgets: oldWidgets,
        },
      };
    });
    // setWidgets((oldWidgets) => {
    //   const widgetsDataArr = Array.isArray(addedWidgetsData)
    //     ? addedWidgetsData
    //     : [addedWidgetsData];
    //   widgetsDataArr.forEach((widgetData) => {
    //     const newIndex =
    //       widgetData.insertIndex >= 0
    //         ? widgetData.insertIndex
    //         : oldWidgets[widgetData.parent]?.children.length;
    //     oldWidgets[widgetData.parent]?.children.splice(newIndex, 0, widgetData.id);
    //     oldWidgets[widgetData.id] = widgetData;
    //   });
    //   return { ...oldWidgets };
    // });
    setHasUnsavedChanges(true);
  };

  const handleWidgetAddFromDrawerWithConfig = (e: any, widget: any, widgetProp: any) => {
    const addedWidgetJSON = addWidgetToLayoutFromDrawer(e, widget);
    addedWidgetJSON['props'] = { ...addedWidgetJSON['props'], ...widgetProp };
    addedWidgetJSON['insertIndex'] =
      widgetContextState.indexToAddWidgetAtFromMainSection !== null
        ? widgetContextState.indexToAddWidgetAtFromMainSection
        : getWidgetsOnCurrentRoute()['page-builder']?.children?.length;
    if (addedWidgetJSON) {
      addWidget(addedWidgetJSON);
      const activeWidget = Array.isArray(addedWidgetJSON)
        ? addedWidgetJSON[0]
        : addedWidgetJSON;
      setActiveWidget(activeWidget.id);
      setIndexToAddWidgetAtFromMainSection(null);
      toggleSidePanelState('widgetProperty');
    }
  };

  const handleWidgetAdd = (e: SortableEvent) => {
    const isFromWidgetList = e.from.classList.contains('widgetGroupList');
    if (!isFromWidgetList) {
      return;
    }
    const addedWidgetJSON = addWidgetToLayout(e, handleWidgetAdd);
    if (addedWidgetJSON) {
      addWidget(addedWidgetJSON);
      const activeWidget = Array.isArray(addedWidgetJSON)
        ? addedWidgetJSON[0]
        : addedWidgetJSON;
      setActiveWidget(activeWidget.id);
    }
  };

  const addWidgetViaMainSection = (
    indexToInsertAt: number,
    widgetData?: any,
    parentId?: any
  ) => {
    setIndexToAddWidgetAtFromMainSection(indexToInsertAt);
    toggleSidePanelState('widgetDrawerSection');
  };

  const handleWidgetSelect = (widget?: any) => {
    const widgetId = widget?.id;
    setActiveWidget(widgetId);
    setIsHeaderFooterPropertyActive({
      status: false,
      type: null,
    });
    toggleSidePanelState('widgetProperty');
  };

  const handleBuilderSectionSelect = (params = {} as any) => {
    const { widget, leftPanelType = 'widgetProperty', leftPanelBuilderConfigID } = params;
    if (widget) {
      const widgetId = widget?.id;
      setActiveWidget(widgetId);
    }
    setIsHeaderFooterPropertyActive({
      status: false,
      type: null,
    });
    // handleWidgetPropChange(
    //   {
    //     subWidget: '',
    //     subWidgetIndex: -1,
    //   },
    //   widgetId //  activeWidgetId
    // );
    toggleSidePanelState(leftPanelType, leftPanelBuilderConfigID);
  };

  const removeWidgetRecursive = (
    oldWidgets: Record<string, WidgetJSONData>,
    widgetId: string
  ) => {
    const widget = oldWidgets[widgetId];
    if (!widget) {
      return oldWidgets;
    }
    const parent = oldWidgets[oldWidgets[widgetId].parent];
    parent.children = parent.children.filter((child) => !!oldWidgets[child]);
    const indexInParent = parent.children.indexOf(widgetId);
    parent.children.splice(indexInParent, 1);
    parent.children = [...parent.children];
    delete oldWidgets[widgetId];
    widget.children.forEach((child) => removeWidgetRecursive(oldWidgets, child));

    return oldWidgets;
  };

  const removeWidget = (widgetId: string) => {
    setWidgetsByRoute(({ widgetsByRoute, currentSelectedRoute }) => {
      const oldWidgets = widgetsByRoute[currentSelectedRoute]?.widgets;
      return {
        routeName: currentSelectedRoute,
        routeConfigs: {
          ...widgetsByRoute[currentSelectedRoute],
          widgets: { ...removeWidgetRecursive(oldWidgets, widgetId) },
        },
      };
    });
    // setWidgets((oldWidgets) => {
    //   return { ...removeWidgetRecursive(oldWidgets, widgetId) };
    // });
    setHasUnsavedChanges(true);
  };

  const handleDeleteIconClick = (widget?: any) => {
    const widgetId = widget?.id;
    setWidgetIdToDelete(widgetId);
    setWidgetDeleteModalOpen(true);
  };

  const handleDelete = (widgetId?: any) => {
    // const widgets = e.currentTarget.closest('[data-widget]') as HTMLDivElement;
    removeWidget(widgetId || widgetContextState.widgetIdToDelete);
    setWidgetDeleteModalOpen(false);
    setWidgetIdToDelete(null);
    if (widgetContextState.activeWidgetId === widgetContextState.widgetIdToDelete) {
      setActiveWidget('');
    }
    toggleSidePanelState('widgetOutline');
  };

  const handleUpDownMove = (widgetId: string, direction: string) => {
    setWidgetsByRoute(({ widgetsByRoute, currentSelectedRoute }) => {
      const oldWidgets = widgetsByRoute[currentSelectedRoute]?.widgets;
      const widgetParentId = oldWidgets[widgetId].parent;
      const widgetParent = oldWidgets[widgetParentId];
      const oldIndex = widgetParent.children.indexOf(widgetId);
      const newIndex = direction === 'up' ? oldIndex - 1 : oldIndex + 1;
      const newChildren = changeArrayIndex(widgetParent.children, oldIndex, newIndex);
      widgetParent.children = newChildren;
      return {
        routeName: currentSelectedRoute,
        routeConfigs: {
          ...widgetsByRoute[currentSelectedRoute],
          widgets: { ...oldWidgets },
        },
      };
    });
    // setWidgets((oldWidgets) => {
    //   const widgetParentId = oldWidgets[widgetId].parent;
    //   const widgetParent = oldWidgets[widgetParentId];
    //   const oldIndex = widgetParent.children.indexOf(widgetId);
    //   const newIndex = direction === 'up' ? oldIndex - 1 : oldIndex + 1;
    //   const newChildren = changeArrayIndex(widgetParent.children, oldIndex, newIndex);
    //   widgetParent.children = newChildren;
    //   return { ...oldWidgets };
    // });
  };

  const handleWidgetAddFromDrawer = (e: any, widget: any) => {
    const addedWidgetJSON = addWidgetToLayoutFromDrawer(e, widget);
    let insertIndex =
      widgetContextState.indexToAddWidgetAtFromMainSection !== null
        ? widgetContextState.indexToAddWidgetAtFromMainSection
        : getWidgetsOnCurrentRoute()['page-builder']?.children?.length;
    if (widget.name === ADMIN_WIDGETS.HERO_BANNER.name) {
      insertIndex = 0;
    }
    addedWidgetJSON['insertIndex'] = insertIndex;

    if (addedWidgetJSON) {
      addWidget(addedWidgetJSON);
      const activeWidget = Array.isArray(addedWidgetJSON)
        ? addedWidgetJSON[0]
        : addedWidgetJSON;
      setActiveWidget(activeWidget.id);
      toggleSidePanelState('widgetProperty');
    }
  };

  const handleReorderWidgetSidebar = (e: SortableEvent, parentId: string, fn: any) => {
    const { oldIndex, newIndex } = e;
    const parentWidgetId = parentId;
    setWidgetsByRoute(({ widgetsByRoute, currentSelectedRoute }) => {
      const oldWidgets = widgetsByRoute[currentSelectedRoute]?.widgets;
      const parentWidget = oldWidgets[parentWidgetId];
      parentWidget.children = changeArrayIndex(parentWidget.children, oldIndex, newIndex);
      return {
        routeName: currentSelectedRoute,
        routeConfigs: {
          ...widgetsByRoute[currentSelectedRoute],
          widgets: { ...oldWidgets },
        },
      };
    });
    // setWidgets((oldWidgets) => {
    //   const parentWidget = oldWidgets[parentWidgetId];
    //   parentWidget.children = changeArrayIndex(parentWidget.children, oldIndex, newIndex);
    //   return { ...oldWidgets };
    // });
    fn(getWidgetsOnCurrentRoute());
    setHasUnsavedChanges(true);
  };

  const redirectToWidgetOutline = () => {
    toggleSidePanelState('widgetOutline');
    setActiveWidget('');
    setIsHeaderFooterPropertyActive({
      status: false,
      type: null,
    });
  };

  return {
    toggleSidePanelState,
    getStylesOnCurrentRoute,
    getBuilderConfigsOnCurrentRoute,
    handleWidgetPropChangeForBasicWidgets,
    handlePublish,
    handleWidgetPropChange,
    handleWidgetAddFromDrawerWithConfig,
    addWidget,
    handleWidgetAdd,
    addWidgetViaMainSection,
    handleWidgetSelect,
    handleBuilderSectionSelect,
    handleDeleteIconClick,
    removeWidgetRecursive,
    removeWidget,
    handleDelete,
    handleUpDownMove,
    handleWidgetAddFromDrawer,
    handleReorderWidgetSidebar,
    redirectToWidgetOutline,
    getWidgetsOnCurrentRoute,
    handleRouteStyleChange,
  };
};
